import { cva, type VariantProps } from 'class-variance-authority';
import {
  cloneElement,
  type JSX,
  type ReactElement,
  type CSSProperties,
} from 'react';

import { cn } from './utils';
import { wrapArray } from './arrayUtils';

export const iconVariants = cva(
  'w-[var(--svg-width)] h-[var(--svg-height)] text-carbon-500',
  {
    variants: {
      variant: {
        default: 'fill-[var(--svg-color,currentColor)] stroke-transparent',
        fill: 'fill-[var(--svg-color,currentColor)] stroke-transparent',
        stroke: 'stroke-[var(--svg-color,currentColor)] fill-transparent',
        none: 'fill-unset stroke-unset',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface ICreateIconParams extends VariantProps<typeof iconVariants> {
  viewBox: string;
  path?: ReactElement | ReactElement[];
}

export type TIcon = (props: IIconProps) => JSX.Element;

export interface IIconProps extends VariantProps<typeof iconVariants> {
  color?: string;
  className?: string;
  w?: number | string;
  h?: number | string;
  size?: number | string;
}

export function createIcon({
  path,
  viewBox,
  variant: defaultVariant,
}: ICreateIconParams): TIcon {
  const paths = wrapArray(path);

  const Component = ({
    size,
    color,
    w = 24,
    h = 24,
    variant,
    className,
  }: IIconProps) => {
    const width = size ?? w;
    const height = size ?? h;

    return (
      <svg
        viewBox={viewBox}
        xmlns='http://www.w3.org/2000/svg'
        className={cn(
          iconVariants({
            className,
            variant: variant || defaultVariant,
          }),
        )}
        style={
          {
            ...(color ? { '--svg-color': color || 'currentColor' } : {}),
            '--svg-width': typeof width === 'string' ? width : `${width}px`,
            '--svg-height': typeof height === 'string' ? height : `${height}px`,
          } as CSSProperties
        }
      >
        {paths.map((p, index) => {
          return cloneElement(p, {
            key: index,
          });
        })}
      </svg>
    );
  };

  return Component;
}
